import { startOfHour, subDays } from 'date-fns';

import {
  AggregationIntervals,
  type EnergyConsumptionQuery,
  MediumType,
  TimeSpans,
  useEnergyConsumptionQuery,
} from '@/__generated__/types';
import type { DashboardPeriod } from '@/constants/dashboard-period';

import { UNRESOLVED_SCOPE } from '../constants';
import type { SpaceScope } from '../types/home-types';

export interface UseEnergyConsumptionParams {
  scope?: SpaceScope;
  timeSpan: DashboardPeriod;
}

const MEDIUMS = [
  MediumType.ElectricalEnergy,
  MediumType.Gas,
  MediumType.Heating,
  MediumType.Cooling,
];

const AGGREGATION_INTERVAL = AggregationIntervals.D_01;

const PERIOD_DAYS: Record<DashboardPeriod, number> = {
  [TimeSpans.Last_7Days]: 7,
  [TimeSpans.Last_30Days]: 30,
  [TimeSpans.Last_366Days]: 366,
};

type EnergySeries = EnergyConsumptionQuery['consumption']['data'];

/** Total of every point across the series sharing the first series' unit. */
export const sumSeries = (series: EnergySeries) => {
  const unit = series[0]?.unit ?? null;
  const total = series
    .filter((item) => item.unit === unit)
    .reduce(
      (acc, item) =>
        acc + item.data.reduce((sum, point) => sum + point.value, 0),
      0,
    );

  return { total: unit === null ? null : total, unit };
};

/** Percent change versus the previous period; null when there is nothing to compare against. */
export const getTrendPercent = (
  current: number | null,
  previous: number | null,
) =>
  current === null || previous === null || previous === 0
    ? null
    : ((current - previous) / previous) * 100;

export const useEnergyConsumption = ({
  scope,
  timeSpan,
}: UseEnergyConsumptionParams) => {
  const days = PERIOD_DAYS[timeSpan];
  // Truncated to the hour so the previous-window variables stay stable across renders.
  const now = startOfHour(new Date());

  const { data, loading, error, refetch } = useEnergyConsumptionQuery({
    skip: !scope,
    variables: {
      ...(scope ?? UNRESOLVED_SCOPE),
      mediums: MEDIUMS,
      current: {
        timeSpan,
        readUntilNow: true,
        aggregationIntervalTypeActive: AGGREGATION_INTERVAL,
      },
      previous: {
        readFromUtc: subDays(now, days * 2).toISOString(),
        readUntilUtc: subDays(now, days).toISOString(),
        aggregationIntervalTypeActive: AGGREGATION_INTERVAL,
      },
    },
    errorPolicy: 'all',
  });

  const consumption = sumSeries(data?.consumption?.data ?? []);
  const previous = sumSeries(data?.previous?.data ?? []);
  const costs = sumSeries(data?.costs?.data ?? []);

  return {
    total: consumption.total,
    unit: consumption.unit,
    trendPercent: getTrendPercent(consumption.total, previous.total),
    cost: costs.total,
    costUnit: costs.unit,
    isLoading: loading || !scope,
    isError: !!error,
    retry: () => {
      void refetch();
    },
  };
};
